import React from "react";
import { chipClass, ChipTone } from "./index";

/**
 * Session-aware freshness — ONE shared source of truth.
 *
 * Was duplicated inline in RiskDashboard + PortfolioView. EOD/daily feeds land
 * the next morning, so "age" is measured in US trading SESSIONS, not clock
 * hours: Friday's close viewed Monday is "1 session ago", not "3 days ago".
 * Holidays are NOT modeled client-side; the per-feed `graceSessions` cushion
 * absorbs the occasional holiday so we under-alarm by a day rather than cry wolf.
 */

// Calendar parts for an epoch ms in US/Eastern (handles EST/EDT automatically).
export function etParts(ms: number): { y: number; m: number; d: number; hour: number } {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    hour12: false,
  });
  const get = (t: string) =>
    fmt.formatToParts(new Date(ms)).find((p) => p.type === t)?.value ?? "";
  return { y: Number(get("year")), m: Number(get("month")), d: Number(get("day")), hour: Number(get("hour")) };
}

// Whole-day serial number for an ET calendar date (days since 1970-01-01).
export function etDateSerial(p: { y: number; m: number; d: number }): number {
  return Math.floor(Date.UTC(p.y, p.m - 1, p.d) / 86400000);
}

// US weekdays (Mon–Fri) strictly after the data's ET session date, up to today.
// Friday data viewed Monday → 1; Friday data viewed Wednesday → 3.
export function sessionsBehind(dataMs: number, nowMs: number): number {
  const dataSerial = etDateSerial(etParts(dataMs));
  const nowSerial = etDateSerial(etParts(nowMs));
  if (nowSerial <= dataSerial) return 0;
  let count = 0;
  for (let s = dataSerial + 1; s <= nowSerial; s++) {
    const weekday = (((s + 4) % 7) + 7) % 7; // serial 0 = 1970-01-01 = Thursday(4); 0=Sun..6=Sat
    if (weekday !== 0 && weekday !== 6) count++;
  }
  return count;
}

// Stale once the data is more than `graceSessions` trading sessions behind.
export function sessionStale(ms: number | null, graceSessions: number): boolean {
  if (ms === null) return true; // unknown age is treated as not-confirmed-fresh
  return sessionsBehind(ms, Date.now()) > graceSessions;
}

// EOD feeds report a bare date (YYYY-MM-DD); NAV reports a real intraday timestamp.
export function isDateOnlyTs(s?: string | null): boolean {
  return typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s);
}

// Calendar date (no time) of an epoch ms in UTC.
export function fmtUtcDate(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, {
    timeZone: "UTC",
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
}

// Session-aware age label for EOD/date feeds.
export function sessionAgeLabel(ms: number): string {
  const n = sessionsBehind(ms, Date.now());
  if (n <= 0) return "current";
  return n === 1 ? "1 session ago" : `${n} sessions ago`;
}

export function isWeekendNow(): boolean {
  const d = etParts(Date.now());
  const weekday = (((etDateSerial(d) + 4) % 7) + 7) % 7;
  return weekday === 0 || weekday === 6;
}

/**
 * Standard freshness chip: status word + session-age + as-of date in the tooltip.
 * Color follows the shared convention: fresh=positive, aging=warning, stale=danger,
 * unknown=neutral. Use the SAME `graceSessions` the page used inline so behavior
 * is unchanged when a page is migrated onto this component.
 */
export const FreshnessChip: React.FC<{
  ms: number | null;
  graceSessions?: number;
  label?: string;
  asOf?: string | null;
  className?: string;
}> = ({ ms, graceSessions = 2, label, asOf, className }) => {
  const stale = sessionStale(ms, graceSessions);
  const age = ms == null ? "unknown" : sessionAgeLabel(ms);
  const tone: ChipTone =
    ms == null ? "neutral" : stale ? "danger" : age === "current" ? "positive" : "warning";
  const title =
    (label ? `${label} — ` : "") +
    (ms == null ? "age unknown" : `data ${age}`) +
    (asOf ? ` · as of ${asOf}` : "") +
    (stale ? ` · stale (> ${graceSessions} sessions behind)` : "");
  const text = (label ? label + " " : "") + age;
  return (
    <span className={chipClass(tone, className)} title={title}>
      {text}
    </span>
  );
};
